import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Plus, X, RotateCcw, AlertTriangle, DollarSign, Package, Search } from "lucide-react";
import SyncBadge from "../../components/SyncBadge";

// ─── KPI tile ────────────────────────────────────────────────────────
function KpiTile({ icon: Icon, label, value, accentColor, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef", borderTop: `3px solid ${accentColor}` }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: iconBg }}>
          <Icon className="h-5 w-5" style={{ color: iconColor }} />
        </div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] mb-1" style={{ color: "#94a3b8" }}>{label}</div>
      <div className="font-heading text-[26px] font-bold leading-none" style={{ color: "#0f172a" }}>{value}</div>
    </div>
  );
}

export default function AdminCredits() {
  const [data,  setData]  = useState({ items: [], totals: { count: 0, total_credit: 0, returned_inventory_value: 0, lost_damaged_value: 0 } });
  const [showForm, setShowForm] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [form,  setForm]  = useState({ order_id: "", reason: "", restock: true, condition_on_return: "used", notes: "" });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  // ── ALL LOGIC UNTOUCHED ──────────────────────────────────────────
  const load = async () => {
    const { data } = await adminApi.get("/admin/credits");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const openForm = async () => {
    const { data } = await adminApi.get("/admin/orders");
    const eligible = (data.items || []).filter(
      (o) => ["paid", "cod_pending"].includes(o.payment_status) && o.credit_status !== "full"
    );
    setOrders(eligible);
    setShowForm(true);
  };

  const pickOrder = (o) => {
    setSelectedOrder(o);
    setForm((f) => ({ ...f, order_id: o.id }));
    setItems((o.items || []).map((i) => ({ ...i, qty: 0, selected: false })));
  };

  const submit = async (e) => {
    e.preventDefault();
    const chosen = items.filter((i) => i.selected && i.qty > 0).map((i) => ({
      sku: i.sku, product_id: i.product_id, name: i.name, qty: i.qty, price: i.price,
    }));
    if (chosen.length === 0) { toast.error("Select at least one item to credit"); return; }
    try {
      const { data } = await adminApi.post("/admin/credits", {
        order_id: form.order_id,
        items: chosen,
        reason: form.reason,
        restock: form.restock,
        condition_on_return: form.condition_on_return,
        notes: form.notes,
      });
      toast.success(`Credit ${data.credit_number} issued for ${formatAud(data.amount)}`);
      setShowForm(false); setSelectedOrder(null); setItems([]);
      setForm({ order_id: "", reason: "", restock: true, condition_on_return: "used", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to issue credit");
    }
  };
  // ── END LOGIC ────────────────────────────────────────────────────

  const t = data.totals;
  const filteredCredits = search
    ? data.items.filter((c) =>
        [c.credit_number, c.order_number, c.customer_name, c.customer_email]
          .some((v) => v?.toLowerCase().includes(search.toLowerCase()))
      )
    : data.items;

  const filteredOrders = orderSearch
    ? orders.filter((o) =>
        [o.order_number, o.customer_name, o.customer_email].some((v) => v?.toLowerCase().includes(orderSearch.toLowerCase()))
      )
    : orders;

  const inp = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };

  return (
    <div className="space-y-5" data-testid="admin-credits-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#94a3b8" }}>Finance</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>Credits & Returns</h1>
          <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
            Track invoice credits, restocked returns, and lost/damaged inventory
          </p>
        </div>
        <Button onClick={openForm}
          className="h-10 rounded-xl text-white font-semibold text-[13px]"
          style={{ background: "#e31e24" }}
          data-testid="credit-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Issue Credit
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile icon={CreditCard}    label="Credits Issued"      value={t.count}                             accentColor="#e31e24" iconBg="#fef2f2" iconColor="#e31e24" />
        <KpiTile icon={DollarSign}    label="Total Credit Value"  value={formatAud(t.total_credit)}           accentColor="#d97706" iconBg="#fffbeb" iconColor="#d97706" />
        <KpiTile icon={RotateCcw}     label="Returned to Stock"   value={formatAud(t.returned_inventory_value)} accentColor="#16a34a" iconBg="#f0fdf4" iconColor="#16a34a" />
        <KpiTile icon={AlertTriangle} label="Lost / Damaged"      value={formatAud(t.lost_damaged_value)}     accentColor="#ea580c" iconBg="#fff7ed" iconColor="#ea580c" />
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94a3b8" }} />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search credit #, order #, customer…"
          className="w-full pl-10 pr-4 h-10 rounded-xl text-[13px] outline-none"
          style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
      </div>

      {/* Credits table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm overflow-x-auto" style={{ border: "1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#f5f7fb", borderBottom: "1px solid #dde3ef" }}>
              {["Credit #", "Order", "Customer", "Items", "Outcome", "Sync", "Amount", "Date"].map((h, i) => (
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color: "#475569", fontSize: 10, letterSpacing: "0.15em", textAlign: i >= 6 ? "right" : "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCredits.map((c, idx) => (
              <tr key={c.id}
                className="hover:bg-[#f5f7fb] transition-colors"
                style={{ borderBottom: idx < filteredCredits.length - 1 ? "1px solid #f0f4f8" : "none" }}
                data-testid={`credit-row-${c.credit_number}`}>

                <td className="px-4 py-3">
                  <div className="font-heading font-bold text-[13px]" style={{ color: "#0f172a" }}>{c.credit_number}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[13px]" style={{ color: "#0f172a" }}>{c.order_number}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-[13px]" style={{ color: "#0f172a" }}>{c.customer_name}</div>
                  <div className="text-[11px]" style={{ color: "#64748b" }}>{c.customer_email}</div>
                </td>
                <td className="px-4 py-3 max-w-[160px]">
                  <div className="text-[11px] font-mono truncate" style={{ color: "#64748b" }}>
                    {c.items.map((i) => `${i.qty}× ${i.sku}`).join(", ")}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {c.restock ? (
                    <span className="badge badge-green">
                      <RotateCcw className="h-2.5 w-2.5" /> Restocked
                    </span>
                  ) : (
                    <span className="badge badge-red">
                      <AlertTriangle className="h-2.5 w-2.5" /> Damaged
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <SyncBadge
                    status={c.sync_status || "pending"}
                    lastSyncedAt={c.last_synced_at}
                    syncError={c.sync_error}
                    compact
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="font-heading font-bold text-[15px]" style={{ color: "#e31e24" }}>
                    {formatAud(c.amount)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>
                    {new Date(c.created_at).toLocaleDateString("en-AU")}
                  </div>
                </td>
              </tr>
            ))}
            {filteredCredits.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                  {search ? "No credits match your search" : "No credits issued yet"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Issue credit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowForm(false)}
          data-testid="credit-form-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
            style={{ border: "1px solid #dde3ef" }}
            onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="p-5 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl"
              style={{ borderBottom: "1px solid #eef2f8" }}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>New Credit</div>
                <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>Issue invoice credit</div>
              </div>
              <Button variant="ghost" onClick={() => setShowForm(false)} style={{ color: "#475569" }}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={submit} className="p-5 space-y-5">
              {!selectedOrder ? (
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                    Select an eligible order
                  </Label>
                  {/* Order search */}
                  <div className="relative mt-2 mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "#94a3b8" }} />
                    <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Search orders…"
                      className="w-full pl-9 h-9 rounded-xl text-[13px] outline-none"
                      style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#0f172a" }} />
                  </div>
                  <div className="max-h-72 overflow-y-auto rounded-xl" style={{ border: "1px solid #dde3ef" }}>
                    {filteredOrders.length === 0 ? (
                      <div className="p-6 text-center text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                        No eligible orders
                      </div>
                    ) : filteredOrders.map((o, idx) => (
                      <button key={o.id} type="button" onClick={() => pickOrder(o)}
                        className="w-full px-4 py-3 hover:bg-[#f5f7fb] text-left flex items-center justify-between transition-colors"
                        style={{ borderBottom: idx < filteredOrders.length - 1 ? "1px solid #f0f4f8" : "none" }}
                        data-testid={`select-order-${o.order_number}`}>
                        <div>
                          <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{o.order_number}</div>
                          <div className="text-[11px]" style={{ color: "#64748b" }}>{o.customer_name} · {o.customer_email}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{formatAud(o.total)}</div>
                          {o.credit_amount > 0 && (
                            <div className="text-[11px]" style={{ color: "#d97706" }}>Credited: {formatAud(o.credit_amount)}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Selected order badge */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "#eff6ff", border: "1px solid #93c5fd" }}>
                    <div className="flex-1 text-[13px] font-medium" style={{ color: "#1e40af" }}>
                      Order <b>{selectedOrder.order_number}</b> · {selectedOrder.customer_name}
                    </div>
                    <button type="button"
                      onClick={() => { setSelectedOrder(null); setItems([]); }}
                      className="text-[11px] font-mono hover:underline"
                      style={{ color: "#e31e24" }}>
                      Change
                    </button>
                  </div>

                  {/* Items to credit */}
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                      Items to credit
                    </Label>
                    <div className="mt-2 rounded-xl overflow-hidden" style={{ border: "1px solid #dde3ef" }}>
                      {items.map((it, idx) => (
                        <div key={it.sku}
                          className="p-3 flex items-center gap-3 hover:bg-[#f5f7fb]"
                          style={{ borderBottom: idx < items.length - 1 ? "1px solid #f0f4f8" : "none" }}>
                          <input type="checkbox" checked={it.selected}
                            onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx
                              ? { ...x, selected: e.target.checked, qty: e.target.checked ? (x.qty || 1) : 0 }
                              : x
                            ))}
                            className="w-4 h-4 cursor-pointer accent-[#e31e24]"
                            data-testid={`credit-item-toggle-${it.sku}`} />
                          <div className="flex-1">
                            <div className="text-[13px] font-medium" style={{ color: "#0f172a" }}>{it.name}</div>
                            <div className="text-[10px] font-mono" style={{ color: "#94a3b8" }}>{it.sku} · {formatAud(it.price)}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-[10px] font-mono" style={{ color: "#94a3b8" }}>Qty</label>
                            <Input type="number" min="0"
                              max={selectedOrder.items.find((x) => x.sku === it.sku)?.qty || 99}
                              value={it.qty}
                              onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx
                                ? { ...x, qty: Number(e.target.value), selected: Number(e.target.value) > 0 }
                                : x
                              ))}
                              className="w-20 rounded-lg text-right" style={inp}
                              disabled={!it.selected} />
                          </div>
                          <div className="text-[13px] font-mono font-bold text-right" style={{ color: "#e31e24", minWidth: 64 }}>
                            {it.selected && it.qty > 0 ? formatAud(it.price * it.qty) : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Credit total */}
                    {items.some((i) => i.selected && i.qty > 0) && (
                      <div className="mt-2 px-3 py-2 rounded-xl text-right" style={{ background: "#fef2f2", border: "1px solid #fca5a5" }}>
                        <span className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>Credit total: </span>
                        <span className="font-heading font-bold text-[16px]" style={{ color: "#e31e24" }}>
                          {formatAud(items.filter((i) => i.selected).reduce((s, i) => s + (i.price * i.qty), 0))}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Condition + restock */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                        Condition on return
                      </Label>
                      <Select value={form.condition_on_return}
                        onValueChange={(v) => setForm({ ...form, condition_on_return: v, restock: v === "used" ? form.restock : false })}>
                        <SelectTrigger className="mt-1 rounded-xl" style={inp} data-testid="credit-condition">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="used">Used / serviceable</SelectItem>
                          <SelectItem value="damaged">Damaged / unsellable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                        Restock to inventory
                      </Label>
                      <div className="mt-3 flex items-center gap-3">
                        <Switch
                          checked={form.restock && form.condition_on_return === "used"}
                          disabled={form.condition_on_return === "damaged"}
                          onCheckedChange={(v) => setForm({ ...form, restock: v })}
                          data-testid="credit-restock-toggle"
                        />
                        <span className="text-[12px]" style={{ color: form.condition_on_return === "damaged" ? "#94a3b8" : form.restock ? "#16a34a" : "#e31e24" }}>
                          {form.condition_on_return === "damaged"
                            ? "Damaged — cannot restock"
                            : form.restock ? "Stock will be incremented" : "Will NOT increase stock"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Reason + notes */}
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Reason</Label>
                    <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      className="mt-1 rounded-xl" style={inp}
                      placeholder="Customer return / faulty / wrong fit"
                      data-testid="credit-reason" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Internal notes</Label>
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={3} className="w-full mt-1 px-3 py-2 rounded-xl text-sm outline-none resize-none"
                      style={{ ...inp, border: "1px solid #dde3ef" }} />
                  </div>

                  <Button type="submit"
                    className="w-full h-11 rounded-xl text-white font-bold uppercase tracking-wide text-[14px]"
                    style={{ background: "#e31e24" }}
                    data-testid="credit-submit-btn">
                    Issue Credit
                  </Button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}